/* ─────────────────────────────────────────────────────────────────────────
 *  A small, hand-inlined subset of Phosphor Icons (phosphoricons.com), MIT
 *  licensed, "regular" weight — https://github.com/phosphor-icons/core.
 *
 *  Deliberately NOT the @phosphor-icons/react npm package: this app only
 *  needs four glyphs (arrow-right, trend-up, trend-down, minus), so pulling
 *  in the full package for four paths would add a dependency and a lockfile
 *  change for no real benefit. Inlining the exact upstream path data keeps
 *  the same visual contract (regular weight, 16/20/24px, always
 *  `currentColor`) with zero added bundle machinery — the same reasoning as
 *  self-hosting the Open Sans font files instead of a CDN request.
 *
 *  If this app's icon usage grows well past this handful, switch to the real
 *  package instead of inlining more paths by hand.
 * ───────────────────────────────────────────────────────────────────────── */

interface IconProps {
  /** Pixel size — brand scale only allows 16, 20 or 24. */
  size?: 16 | 20 | 24;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

function PhosphorSvg({
  size = 16,
  className,
  d,
  ariaHidden = true,
}: IconProps & { d: string; ariaHidden?: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 256 256"
      fill="currentColor"
      className={className}
      aria-hidden={ariaHidden}
    >
      <path d={d} />
    </svg>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z"
    />
  );
}

export function TrendUpIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M240,56v64a8,8,0,0,1-16,0V75.31l-82.34,82.35a8,8,0,0,1-11.32,0L96,123.31,29.66,189.66a8,8,0,0,1-11.32-11.32l72-72a8,8,0,0,1,11.32,0L136,140.69,212.69,64H168a8,8,0,0,1,0-16h64A8,8,0,0,1,240,56Z"
    />
  );
}

export function TrendDownIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M240,128v64a8,8,0,0,1-8,8H168a8,8,0,0,1,0-16h44.69L136,107.31l-34.34,34.35a8,8,0,0,1-11.32,0l-72-72A8,8,0,0,1,29.66,58.34L96,124.69l34.34-34.35a8,8,0,0,1,11.32,0L224,172.69V128a8,8,0,0,1,16,0Z"
    />
  );
}

export function MinusIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M224,128a8,8,0,0,1-8,8H40a8,8,0,0,1,0-16H216A8,8,0,0,1,224,128Z"
    />
  );
}

export function CaretLeftIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M168.49,199.51a12,12,0,0,1-17,17l-80-80a12,12,0,0,1,0-17l80-80a12,12,0,0,1,17,17L97,128Z"
    />
  );
}

export function CaretRightIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M184.49,136.49l-80,80a12,12,0,0,1-17-17L159,128,87.51,56.49a12,12,0,1,1,17-17l80,80A12,12,0,0,1,184.49,136.49Z"
    />
  );
}

export function DownloadIcon(props: IconProps) {
  return (
    <PhosphorSvg
      {...props}
      d="M224,152v56a16,16,0,0,1-16,16H48a16,16,0,0,1-16-16V152a8,8,0,0,1,16,0v56H208V152a8,8,0,0,1,16,0Zm-101.66,5.66a8,8,0,0,0,11.32,0l40-40a8,8,0,0,0-11.32-11.32L136,132.69V40a8,8,0,0,0-16,0v92.69L93.66,106.34a8,8,0,0,0-11.32,11.32Z"
    />
  );
}

/**
 * "Sliders" filter icon (three bars, each with an offset knob) — the RAW-tab
 * filter trigger. Built from plain shape primitives rather than a hand-
 * transcribed Phosphor path: this glyph's exact look (bar/knob positions) isn't
 * load-bearing the way brand marks are, so a simple, guaranteed-correct
 * construction beats risking a mistyped path string for one icon.
 */
export function FilterIcon({ size = 16, className, "aria-hidden": ariaHidden = true }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor" className={className} aria-hidden={ariaHidden}>
      <rect x="32" y="56" width="192" height="12" rx="6" />
      <circle cx="104" cy="62" r="22" />
      <rect x="32" y="122" width="192" height="12" rx="6" />
      <circle cx="168" cy="128" r="22" />
      <rect x="32" y="188" width="192" height="12" rx="6" />
      <circle cx="88" cy="194" r="22" />
    </svg>
  );
}
